"use client";

import { motion } from "framer-motion";
import { Globe, ImageIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { FadeImage } from "@/components/ui/fade-image";
import { Link } from "@/i18n/navigation";
import { easeSmooth } from "@/lib/animations";
import type { RecipeTileData } from "@/lib/recipes/types";
import { cn } from "@/lib/utils";

const DEFAULT_ASPECT_RATIO = 4 / 5;

export interface RecipeTileVisualData {
  title: string;
  image: string | null;
  imageAspectRatio: number | null;
  publishedAt?: string | null;
}

interface RecipeTileVisualProps {
  recipe: RecipeTileVisualData;
  aspectRatio?: number;
  sizes?: string;
  priority?: boolean;
  compact?: boolean;
  titleClassName?: string;
  hoverScale?: boolean;
  className?: string;
  draftLabel?: string;
  isPublic?: boolean;
}

export function RecipeTileVisual({
  recipe,
  aspectRatio: forcedRatio,
  sizes = "(max-width: 768px) 50vw, (max-width: 1280px) 33vw, 320px",
  priority = false,
  compact = false,
  titleClassName,
  hoverScale = false,
  className: containerClassName,
  draftLabel,
  isPublic = false,
}: RecipeTileVisualProps) {
  const [imageLoaded, setImageLoaded] = useState(priority);
  // A dead image URL shows the no-photo placeholder, not the browser's broken-image icon.
  const [imageFailed, setImageFailed] = useState(false);
  const image = imageFailed ? null : recipe.image;
  const aspectRatio =
    forcedRatio ??
    (recipe.image ? (recipe.imageAspectRatio ?? DEFAULT_ASPECT_RATIO) : DEFAULT_ASPECT_RATIO);

  const showOverlay = image && imageLoaded;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg",
        !compact && "sm:rounded-xl",
        hoverScale && "group",
        containerClassName
      )}
      style={{ aspectRatio }}
    >
      {image ? (
        <FadeImage
          src={image}
          alt={recipe.title}
          fill
          sizes={sizes}
          className={cn(
            "object-cover",
            hoverScale && "transition-transform duration-200 ease-out group-hover:scale-[1.03]"
          )}
          priority={priority}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground",
            hoverScale && "transition-transform duration-200 ease-out group-hover:scale-[1.03]"
          )}
        >
          <ImageIcon
            className="opacity-30"
            style={{ width: "33%", height: "33%" }}
            strokeWidth={1}
            aria-hidden
          />
        </div>
      )}
      {isPublic && (
        <div className="absolute start-1.5 top-1.5 z-10 flex size-5 items-center justify-center rounded-full bg-black/60 text-white shadow-sm backdrop-blur-sm">
          <Globe className="size-3" strokeWidth={2} aria-hidden />
        </div>
      )}
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 text-start transition-opacity duration-400",
          compact ? "px-1.5 pb-1.5 pt-8" : "px-2 pb-2 pt-12 sm:px-3 sm:pb-3 sm:pt-16",
          showOverlay ? "opacity-100" : image ? "opacity-0" : "opacity-100"
        )}
        style={
          image
            ? { background: "linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)" }
            : undefined
        }
      >
        <span
          className={cn(
            "line-clamp-2 leading-snug",
            image ? "text-stone-100" : "text-foreground",
            compact && "text-sm",
            titleClassName
          )}
        >
          {recipe.title}
        </span>
        {draftLabel && recipe.publishedAt === null && (
          <span className="mt-0.5 block text-xs font-semibold uppercase tracking-wide text-amber-400">
            {draftLabel}
          </span>
        )}
      </div>
    </div>
  );
}

interface RecipeTileProps {
  recipe: RecipeTileData;
  className?: string;
  priority?: boolean;
  compact?: boolean;
  sizes?: string;
  aspectRatio?: number;
}

export function RecipeTile({
  recipe,
  className,
  priority = false,
  compact,
  sizes,
  aspectRatio,
}: RecipeTileProps) {
  const t = useTranslations("recipeDetail");
  const isDraft = recipe.publishedAt === null;

  return (
    <Link
      href={`/recipes/${recipe.id}`}
      className={cn("block break-inside-avoid", className)}
      prefetch={false}
    >
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={easeSmooth}>
        <RecipeTileVisual
          recipe={recipe}
          aspectRatio={aspectRatio}
          priority={priority}
          compact={compact}
          sizes={sizes}
          draftLabel={isDraft ? t("draftBadge") : undefined}
        />
      </motion.div>
    </Link>
  );
}
